import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  Shield, 
  Settings, 
  AlertTriangle, 
  CheckCircle, 
  RefreshCw,
  Bot,
  Sparkles
} from 'lucide-react';
import { getHotelPolicy, resetPolicyToDefaults } from '@/lib/policyApi';
import { toast } from 'sonner';
import AgentPolicyCard from '@/components/policy/AgentPolicyCard';

const PolicyManagementPage = () => {
  const { hotelId } = useParams();
  const [policy, setPolicy] = useState(null);
  const [loading, setLoading] = useState(true);
  const [resetting, setResetting] = useState(false);

  useEffect(() => {
    fetchPolicy();
  }, [hotelId]);

  const fetchPolicy = async () => {
    try {
      setLoading(true);
      const response = await getHotelPolicy(hotelId);
      
      if (response.success) {
        setPolicy(response.policy);
      }
    } catch (error) {
      console.error('Error fetching policy:', error);
      toast.error('Failed to load policy configuration');
    } finally {
      setLoading(false);
    }
  };

  const handleResetToDefaults = async () => {
    if (!window.confirm('Are you sure you want to reset all agent policies to default settings? This will overwrite all customizations.')) {
      return;
    }

    try {
      setResetting(true);
      const response = await resetPolicyToDefaults(hotelId);
      
      if (response.success) {
        toast.success('Policy reset to defaults successfully');
        await fetchPolicy();
      }
    } catch (error) {
      console.error('Error resetting policy:', error);
      toast.error('Failed to reset policy');
    } finally {
      setResetting(false);
    }
  };

  const agentInfo = {
    bitsy: {
      name: 'Bitsy',
      icon: Sparkles,
      color: 'text-pink-500',
      bgColor: 'bg-pink-50',
      description: 'Concierge & Guest Services Agent',
      capabilities: 'Handles guest requests, recommendations, and concierge services'
    },
    marina: {
      name: 'Marina',
      icon: Bot,
      color: 'text-blue-500',
      bgColor: 'bg-blue-50',
      description: 'Booking Management Specialist',
      capabilities: 'Manages booking modifications, room changes, and reservations'
    },
    sage: {
      name: 'Sage',
      icon: Shield,
      color: 'text-purple-500',
      bgColor: 'bg-purple-50',
      description: 'Operations & Strategy Agent',
      capabilities: 'Handles maintenance tickets, analytics, and operational insights'
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center space-y-4">
          <RefreshCw className="h-8 w-8 animate-spin mx-auto text-gray-400" />
          <p className="text-sm text-gray-500">Loading policy configuration...</p>
        </div>
      </div>
    );
  }

  if (!policy) {
    return (
      <div className="p-8">
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>
            Failed to load policy configuration. Please try refreshing the page.
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 p-6">
      <div className="max-w-7xl mx-auto space-y-6">
        
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="space-y-1">
            <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
              <Shield className="h-8 w-8 text-blue-600" />
              Agent Policy Management
            </h1>
            <p className="text-gray-500">
              Configure agent capabilities, tools, and usage limits for your hotel
            </p>
          </div>
          
          <Button
            variant="outline"
            onClick={handleResetToDefaults}
            disabled={resetting}
            className="gap-2"
            data-testid="reset-defaults-btn"
          >
            <RefreshCw className={`h-4 w-4 ${resetting ? 'animate-spin' : ''}`} />
            Reset to Defaults
          </Button>
        </div>

        {/* Info Alert */}
        <Alert className="bg-blue-50 border-blue-200">
          <CheckCircle className="h-4 w-4 text-blue-600" />
          <AlertDescription className="text-blue-900">
            <strong>Hotel Sovereignty:</strong> Each agent can be customized independently. 
            Changes take effect immediately and only apply to your hotel.
          </AlertDescription>
        </Alert>

        {/* Agent Tabs */}
        <Tabs defaultValue="bitsy" className="w-full">
          <TabsList className="grid w-full grid-cols-3 h-auto p-1">
            {['bitsy', 'marina', 'sage'].map((agent) => {
              const info = agentInfo[agent];
              const rules = policy[`${agent}_rules`];
              const Icon = info.icon;
              
              return (
                <TabsTrigger
                  key={agent}
                  value={agent}
                  className="flex items-center gap-2 p-3 data-[state=active]:bg-white"
                  data-testid={`${agent}-tab`}
                >
                  <Icon className={`h-5 w-5 ${info.color}`} />
                  <div className="flex flex-col items-start">
                    <span className="font-semibold">{info.name}</span>
                    <Badge 
                      variant={rules?.enabled ? "default" : "secondary"}
                      className="text-xs"
                    >
                      {rules?.enabled ? 'Enabled' : 'Disabled'}
                    </Badge>
                  </div>
                </TabsTrigger>
              );
            })}
          </TabsList>

          {/* Agent Configuration Panels */}
          {['bitsy', 'marina', 'sage'].map((agent) => (
            <TabsContent key={agent} value={agent} className="mt-6">
              <AgentPolicyCard
                hotelId={hotelId}
                agentName={agent}
                agentInfo={agentInfo[agent]}
                rules={policy[`${agent}_rules`]}
                onUpdate={fetchPolicy}
              />
            </TabsContent>
          ))}
        </Tabs>

        {/* Global Settings Card */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Settings className="h-5 w-5" />
              Global Settings
            </CardTitle>
            <CardDescription>
              Hotel-wide agent configuration and limits
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1">
                <p className="text-sm font-medium text-gray-700">Max Daily Agent Runs</p>
                <p className="text-2xl font-bold text-gray-900">
                  {policy.max_daily_agent_runs?.toLocaleString() || 'Unlimited'}
                </p>
                <p className="text-xs text-gray-500">
                  Prevents abuse by limiting total agent executions per day
                </p>
              </div>
              
              <div className="space-y-1">
                <p className="text-sm font-medium text-gray-700">Audit Logging</p>
                <div className="flex items-center gap-2">
                  {policy.audit_all_actions ? (
                    <>
                      <CheckCircle className="h-5 w-5 text-green-600" />
                      <span className="text-sm font-medium text-green-600">Enabled</span>
                    </>
                  ) : (
                    <>
                      <AlertTriangle className="h-5 w-5 text-yellow-600" />
                      <span className="text-sm font-medium text-yellow-600">Disabled</span>
                    </>
                  )}
                </div>
                <p className="text-xs text-gray-500">
                  All agent actions are logged to telemetry database
                </p>
              </div>
            </div>

            {/* Spending Limits */}
            <div className="pt-4 border-t">
              <h4 className="text-sm font-semibold text-gray-700 mb-3">
                Agent Commerce Spending Limits (HERMES)
              </h4>
              
              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-1">
                  <p className="text-xs text-gray-500">Daily Limit</p>
                  <p className="text-lg font-bold text-gray-900">
                    {policy.spending_limits?.daily_limit_hermes?.toLocaleString() || '0'} HERMES
                  </p>
                </div>
                
                <div className="space-y-1">
                  <p className="text-xs text-gray-500">Per Transaction</p>
                  <p className="text-lg font-bold text-gray-900">
                    {policy.spending_limits?.per_transaction_limit_hermes?.toLocaleString() || '0'} HERMES
                  </p>
                </div>
                
                <div className="space-y-1">
                  <p className="text-xs text-gray-500">Approval Threshold</p>
                  <p className="text-lg font-bold text-gray-900">
                    {policy.spending_limits?.require_approval_over_hermes?.toLocaleString() || '0'} HERMES
                  </p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Last Updated */}
        <div className="text-center text-sm text-gray-500">
          Last updated: {new Date(policy.updated_at).toLocaleString()}
          {policy.updated_by && ` by ${policy.updated_by}`}
        </div>
      </div>
    </div>
  );
};

export default PolicyManagementPage;
